/**
 * Miroir de l'enveloppe de réponse du backend
 * (`Backend/src/core/http/api-response.ts`).
 *
 * Ces types sont la frontière de confiance du front : toute réponse serveur
 * passe par eux. Les garder synchronisés est ce qui permet de ne jamais
 * manipuler de `any` au-dessus de cette couche.
 */

export interface ApiFieldError {
  field: string;
  message: string;
  code?: string;
}

export interface ApiSuccessBody<T> {
  success: true;
  data: T;
  meta?: ApiPaginationMeta | Record<string, unknown>;
  requestId?: string;
}

export interface ApiErrorBody {
  success: false;
  error: {
    code: string;
    message: string;
    details?: ApiFieldError[];
  };
  requestId?: string;
}

export type ApiBody<T> = ApiSuccessBody<T> | ApiErrorBody;

export interface ApiPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/** Résultat d'une liste paginée, remis à plat pour l'usage dans les composants. */
export interface Paginated<T> {
  items: T[];
  meta: ApiPaginationMeta;
}

/**
 * Codes d'erreur stables du backend (`core/errors/error-code.ts`).
 * C'est sur eux que l'interface décide quoi afficher — jamais sur `message`,
 * qui peut être reformulé côté serveur sans préavis.
 */
export const ApiErrorCode = {
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  REFRESH_TOKEN_REVOKED: 'REFRESH_TOKEN_REVOKED',
  FORBIDDEN: 'FORBIDDEN',
  ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  EMAIL_DELIVERY_FAILED: 'EMAIL_DELIVERY_FAILED',
  STORAGE_UNAVAILABLE: 'STORAGE_UNAVAILABLE',
  /** Émis côté client uniquement : le serveur est injoignable. */
  NETWORK_ERROR: 'NETWORK_ERROR',
} as const;

export type ApiErrorCodeValue = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];
